import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { deviceGroupService, deviceService } from '../services/api';

// Edit Group Modal (simple inline)
const EditGroupModal = ({ group, onClose, onSuccess, onError }) => {
  const [groupName, setGroupName] = useState(group?.groupName || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!groupName.trim()) {
      onError('Group name is required');
      return;
    }
    setLoading(true);
    try {
      const response = await deviceGroupService.update(group.id, { groupName: groupName.trim() });
      onSuccess(response.message || 'Device group updated');
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to update group');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>✏️ Edit Device Group</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Group ID</label>
              <input type="text" value={group?.id || ''} disabled className="input-disabled" />
            </div>
            <div className="form-group">
              <label>Group Name *</label>
              <input
                type="text"
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                minLength={2}
                maxLength={100}
                required
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Devices Modal
const AddDevicesModal = ({ group, onClose, onSuccess, onError }) => {
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    const fetchDevices = async () => {
      setLoading(true);
      try {
        const response = await deviceService.getAll({ pageSize: 100, search: searchTerm });
        setDevices(response.data || []);
      } catch (err) {
        console.error('Error fetching devices:', err);
        onError?.('Failed to load devices');
      } finally {
        setLoading(false);
      }
    };
    fetchDevices();
  }, [searchTerm, onError]);

  const toggleDevice = (deviceId) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId) 
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedDevices.length === 0) {
      onError('Please select at least one device');
      return;
    }
    setSubmitting(true);
    try {
      const response = await deviceGroupService.addDevices(group.id, selectedDevices);
      onSuccess(response.message || `${selectedDevices.length} device(s) added to group`);
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to add devices');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>➕ Add Devices to "{group?.groupName}"</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Search Devices</label>
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="🔍 Search by name, IMEI..."
              />
            </div>
            <div className="form-group">
              <label>Select Devices ({selectedDevices.length} selected)</label>
              {loading ? (
                <div className="loading-inline">Loading devices...</div>
              ) : devices.length === 0 ? (
                <div className="empty-state-small">No devices found</div>
              ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                  {devices.map(device => (
                    <div 
                      key={device.id}
                      onClick={() => toggleDevice(device.id)}
                      style={{
                        padding: '0.75rem',
                        borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                        background: selectedDevices.includes(device.id) ? '#e8f4fd' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                      }}
                    >
                      <input 
                        type="checkbox" 
                        checked={selectedDevices.includes(device.id)}
                        onChange={() => {}}
                      />
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{device.nodeName}</div>
                        <div style={{ fontSize: '0.85rem', color: '#666' }}>
                          {device.imei || device.serialNumber || device.widevineId || '—'}
                          {device.tenantName && <span> • {device.tenantName}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting || selectedDevices.length === 0}>
              {submitting ? 'Adding...' : `Add ${selectedDevices.length} Device(s)`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeviceGroupDetail = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [devices, setDevices] = useState([]);
  // Members belonging to an operator other than the group owner. See fetchDevices.
  const [foreignMembers, setForeignMembers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [removingDeviceId, setRemovingDeviceId] = useState(null);
  
  // Modals state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddDevicesModal, setShowAddDevicesModal] = useState(false);

  const fetchGroup = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await deviceGroupService.getById(groupId);
      setGroup(response.data);
    } catch (err) {
      console.error('Error fetching group:', err);
      setError(err.response?.data?.error || 'Failed to load device group');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  const fetchDevices = useCallback(async () => {
    try {
      const response = await deviceGroupService.getDevices(groupId);
      setDevices(response.data || []);
      // foreignMembers is computed per-member, so it only exists on the members
      // response, not on GET /:groupId. It is kept as its own state rather than
      // merged into `group`: the two fetches run concurrently, and a merge
      // would silently drop the value whenever devices resolved first (group
      // still null) or be overwritten when group resolved second.
      setForeignMembers(response.group?.foreignMembers || 0);
    } catch (err) {
      console.error('Error fetching devices:', err);
    }
  }, [groupId]);

  useEffect(() => {
    fetchGroup();
    fetchDevices();
  }, [fetchGroup, fetchDevices]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => {
        setMessage(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  const handleRemoveDevice = async (deviceId, deviceName) => {
    setRemovingDeviceId(deviceId);
    try {
      await deviceGroupService.removeDevice(groupId, deviceId);
      setMessage(`Device "${deviceName}" removed from group`);
      fetchDevices();
      fetchGroup(); // Update device count
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove device from group');
    } finally {
      setRemovingDeviceId(null);
    }
  };

  const handleDeleteGroup = async () => {
    if (!window.confirm(`Are you sure you want to delete "${group?.groupName}"? Devices will NOT be deleted.`)) {
      return;
    }
    setActionLoading(true);
    try {
      await deviceGroupService.delete(groupId);
      setMessage('Device group deleted');
      setTimeout(() => navigate('/device-groups'), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete group');
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  if (loading) {
    return (
      <div className="user-detail-page">
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  if (error && !group) {
    return (
      <div className="user-detail-page">
        <div className="page-header">
          <Link to="/device-groups" className="back-link">← Back to Device Groups</Link>
        </div>
        <div className="alert alert-error">❌ {error}</div>
      </div>
    );
  }

  return (
    <div className="user-detail-page">
      <div className="page-header">
        <Link to="/device-groups" className="back-link">← Back to Device Groups</Link>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => setShowEditModal(true)}>
            ✏️ Edit Group
          </button>
          <div className="actions-dropdown">
            <button className="btn btn-secondary">
              {actionLoading ? '...' : 'Actions ▼'}
            </button>
            <div className="dropdown-menu">
              <button className="dropdown-item" onClick={() => setShowAddDevicesModal(true)}>
                ➕ Add Devices
              </button>
              <button className="dropdown-item text-danger" onClick={handleDeleteGroup}>
                🗑️ Delete Group
              </button>
            </div>
          </div>
        </div>
      </div>

      {message && <div className="alert alert-success">✅ {message}</div>}
      {error && <div className="alert alert-error">❌ {error}</div>}

      {/* Group Profile Card */}
      <div className="user-profile-card">
        <div className="profile-header">
          <div className="user-avatar large" aria-label={`Avatar for ${group?.groupName}`}>
            👥
          </div>
          <div className="profile-info">
            <h1>
              {group?.groupName || 'Unknown Group'}
              {group?.owner === 'tenant' ? (
                <span
                  title={`Owned by ${group?.tenantName || 'an operator'} — they manage this group in media.pdooh`}
                  style={{
                    marginLeft: 12, background: '#ede7f6', color: '#4527a0',
                    padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem',
                    verticalAlign: 'middle', fontWeight: 600,
                  }}
                >
                  🏢 {group?.tenantName || 'Operator'}
                </span>
              ) : group ? (
                <span
                  title="Global admin group — may span operators"
                  style={{
                    marginLeft: 12, background: '#eceff1', color: '#37474f',
                    padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem',
                    verticalAlign: 'middle', fontWeight: 600,
                  }}
                >
                  🌐 Global
                </span>
              ) : null}
            </h1>
            <p className="email">{devices.length} device{devices.length !== 1 ? 's' : ''} in this group</p>
          </div>
        </div>
      </div>

      {/* An operator-owned group whose membership predates V247 (or was written
          by another path) can hold placements belonging to someone else. Those
          rows are INVISIBLE in media.pdooh, which filters members to the group's
          own tenant — so the operator sees a smaller group than this page does,
          with nothing on either screen explaining the gap. This is the only
          surface that can show it. */}
      {group?.owner === 'tenant' && foreignMembers > 0 && (
        <div className="alert alert-error">
          ⚠️ {foreignMembers} device{foreignMembers !== 1 ? 's' : ''} in this group
          {foreignMembers !== 1 ? ' belong' : ' belongs'} to a different operator than the
          group owner ({group.tenantName || 'unknown'}). {foreignMembers !== 1 ? 'They are' : 'It is'}
          {' '}hidden from the owner in media.pdooh, so their device count will not match this page.
          Remove {foreignMembers !== 1 ? 'them' : 'it'} below to reconcile.
        </div>
      )}

      {/* Group Information Section */}
      <div className="detail-section">
        <h2>Group Information</h2>
        <div className="detail-grid">
          <div className="detail-item">
            <label>Group ID</label>
            <span className="mono">{group?.id || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Name</label>
            <span>{group?.groupName || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Devices</label>
            <span>{group?.deviceCount || devices.length || 0}</span>
          </div>
          <div className="detail-item">
            <label>Created By</label>
            <span>{group?.createdByEmail || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Created</label>
            <span>{formatDate(group?.createdAt)}</span>
          </div>
        </div>
      </div>

      {/* Devices Section */}
      <div className="detail-section">
        <div className="section-header">
          <h2>📱 Devices</h2>
          <button 
            className="btn btn-secondary btn-sm" 
            onClick={() => setShowAddDevicesModal(true)}
          >
            + Add Devices
          </button>
        </div>
        {devices.length === 0 ? (
          <div className="empty-state-small">
            <p>No devices in this group yet.</p>
            <p className="muted">Click "Add Devices" to add devices to this group.</p>
          </div>
        ) : (
          <div className="tenant-list">
            {devices.map((device) => (
              <div key={device.id} className="tenant-assignment-item">
                <div className="tenant-info">
                  <Link to={`/devices/${device.id}`} className="tenant-name">
                    📱 {device.nodeName}
                  </Link>
                  <span className="tenant-plan" style={{ marginLeft: '0.5rem' }}>
                    ({device.imei || device.serialNumber || '—'})
                  </span>
                </div>
                <div className="tenant-role">
                  <span
                    style={{
                      color: device.foreignToGroupOwner ? '#c62828' : '#666',
                      fontSize: '0.85rem',
                      fontWeight: device.foreignToGroupOwner ? 600 : 'normal',
                    }}
                    title={
                      device.foreignToGroupOwner
                        ? `Belongs to ${device.tenantName || 'another operator'}, not to the group owner — hidden from the owner in media.pdooh`
                        : undefined
                    }
                  >
                    {device.foreignToGroupOwner ? '⚠️ ' : ''}
                    {device.tenantName || 'Unassigned'}
                  </span>
                </div>
                <button 
                  className="btn btn-danger btn-sm"
                  onClick={() => handleRemoveDevice(device.id, device.nodeName)}
                  disabled={removingDeviceId === device.id}
                >
                  {removingDeviceId === device.id ? '...' : 'Remove'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Group Modal */}
      {showEditModal && (
        <EditGroupModal
          group={group}
          onClose={() => setShowEditModal(false)}
          onSuccess={(msg) => { setMessage(msg); setShowEditModal(false); fetchGroup(); }}
          onError={setError}
        />
      )}

      {/* Add Devices Modal */}
      {showAddDevicesModal && (
        <AddDevicesModal
          group={group}
          onClose={() => setShowAddDevicesModal(false)}
          onSuccess={(msg) => { setMessage(msg); setShowAddDevicesModal(false); fetchDevices(); fetchGroup(); }}
          onError={setError}
        />
      )}
    </div>
  );
};

export default DeviceGroupDetail;
